import { useState } from 'react'
import { motion } from 'framer-motion'
import { Menu, X, Code2, Sparkles } from 'lucide-react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useDashboardData } from '../../services/dashboardQueries'
import textBanner from '../../assets/images/text-banner-black-min.png'
import { trackCTAClick } from '../../config/analyticsEvents'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { user, loading, getAuthToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const isOnPricingPage = location.pathname === '/pricing'

  // Fetch user plan data when logged in
  const { data: dashboardData, isLoading: isPlanLoading } = useDashboardData(getAuthToken, user?.id, null, {
    enabled: !!user && !!getAuthToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const userPlan = dashboardData?.plan?.name?.toLowerCase() || 'free'
  const isPro = userPlan === 'pro'
  const showUpgrade = !!user && !isPlanLoading && !isPro && !isOnPricingPage

  const handleDashboardClick = () => {
    navigate('/dashboard')
    setIsMenuOpen(false)
  }

  const handleUpgradeClick = () => {
    trackCTAClick('upgrade_to_pro', 'header')
    navigate('/pricing')
    setIsMenuOpen(false)
  }

  const handleCTAClick = () => {
    trackCTAClick(user ? 'dashboard' : 'login', 'header')
    if (user) {
      navigate('/dashboard')
    } else {
      navigate('/auth')
    }
    setIsMenuOpen(false)
  }

  const handleLogoClick = (e) => {
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
    navigate('/')
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-50 via-white to-primary-50/30 border-b border-slate-100/50 backdrop-blur-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick}>
            <motion.div 
              className="flex items-center cursor-pointer relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img 
                src={textBanner} 
                alt="EasyAppIcon API" 
                className="h-8"
              />
              <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                API
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Pricing link */}
            <motion.a
              href="#pricing"
              className="text-gray-700 hover:text-primary-600 transition-colors duration-200 font-medium cursor-pointer"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Pricing
            </motion.a>

            {/* Dashboard link - only show when logged in */}
            {user && (
              <motion.button
                onClick={handleDashboardClick}
                className="text-gray-700 hover:text-primary-600 transition-colors duration-200 font-medium cursor-pointer"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Dashboard
              </motion.button>
            )}
            
            {/* Auth-aware CTA Button */}
            {!user ? (
              <motion.button
                onClick={handleCTAClick}
                className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium cursor-pointer"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
              >
                <span>{loading ? '...' : 'Login'}</span>
              </motion.button>
            ) : showUpgrade && (
              <motion.button
                onClick={handleUpgradeClick}
                className="group relative inline-flex items-center justify-center gap-2 text-white pl-5 pr-5 py-2.5 font-semibold cursor-pointer transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="absolute inset-0 w-full h-full drop-shadow-lg" viewBox="0 0 160 44" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="tagGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id="tagGradientHover" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#2563eb" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                  </defs>
                  <path 
                    d="M12 0 H160 V44 H12 L0 22 Z" 
                    fill="url(#tagGradient)"
                    className="group-hover:hidden"
                  />
                  <path 
                    d="M12 0 H160 V44 H12 L0 22 Z" 
                    fill="url(#tagGradientHover)"
                    className="hidden group-hover:block"
                  />
                </svg>
                <Sparkles className="w-4 h-4 flex-shrink-0 relative z-10" />
                <span className="relative z-10">Upgrade to Pro</span>
              </motion.button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            className="md:hidden py-4 border-t border-gray-200"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col space-y-3">
              {/* Pricing link */}
              <a
                href="#pricing"
                className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors duration-200 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Pricing</span>
              </a>

              {/* Dashboard link - only show when logged in */}
              {user && (
                <button
                  onClick={handleDashboardClick}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors duration-200 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer text-left"
                >
                  <span>Dashboard</span>
                </button>
              )}
              
              {/* Mobile Auth-aware CTA Button */}
              {!user ? (
                <button 
                  onClick={handleCTAClick}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium w-fit cursor-pointer"
                  disabled={loading}
                >
                  <Code2 className="w-4 h-4" />
                  <span>{loading ? '...' : 'Login'}</span>
                </button>
              ) : showUpgrade && (
                <button 
                  onClick={handleUpgradeClick}
                  className="group relative inline-flex items-center justify-center gap-2 text-white pl-5 pr-5 py-2.5 font-semibold cursor-pointer transition-all duration-300"
                >
                  <svg className="absolute inset-0 w-full h-full drop-shadow-lg" viewBox="0 0 160 44" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="tagGradientMobile" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#10b981" />
                      </linearGradient>
                    </defs>
                    <path 
                      d="M12 0 H160 V44 H12 L0 22 Z" 
                      fill="url(#tagGradientMobile)"
                    />
                  </svg>
                  <Sparkles className="w-4 h-4 flex-shrink-0 relative z-10" />
                  <span className="relative z-10">Upgrade to Pro</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  )
}

export default Header